import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as React from 'react';

import { cn } from '@heidi/utils';

import {
  scrollAreaRootClasses,
  scrollAreaViewportClasses,
  scrollBarBaseClasses,
  scrollBarOrientationClasses,
  scrollBarThumbClasses,
} from './scroll-area.styles';
import type { ScrollAreaProps, ScrollBarProps } from './scroll-area.types';

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    {
      className,
      children,
      viewportClassName,
      viewportRef,
      type = 'scroll',
      scrollbarVariant = 'default',
      orientation = 'vertical',
      scrollBarClassName,
      ...props
    },
    ref
  ) => {
    const showVertical = orientation === 'vertical' || orientation === 'both';
    const showHorizontal = orientation === 'horizontal' || orientation === 'both';

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        type={type}
        scrollHideDelay={800}
        className={cn(scrollAreaRootClasses, className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={viewportRef}
          className={cn(scrollAreaViewportClasses, viewportClassName)}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        {showVertical && (
          <ScrollBar
            forceMount
            orientation="vertical"
            variant={scrollbarVariant}
            className={scrollBarClassName}
          />
        )}
        {showHorizontal && (
          <ScrollBar
            forceMount
            orientation="horizontal"
            variant={scrollbarVariant}
            className={scrollBarClassName}
          />
        )}
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  }
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ScrollBarProps
>(({ className, orientation = 'vertical', variant = 'default', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      scrollBarBaseClasses,
      scrollBarOrientationClasses(variant, orientation),
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className={scrollBarThumbClasses(variant)} />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
